'use client';

import { Suspense, useMemo, type ReactElement } from 'react';
import { Await } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { SCAPIShopperStoresSchemas } from 'src/types';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetTitle } from '@/components/ui/sheet';
import { DismissCloseButton } from '@/components/ui/dismiss-close-button';
import { Button } from '@/components/ui/button';
import { DynamicImage } from '@/components/dynamic-image';
import { NewTabAnchor } from '@/components/link';
import type { DynamicImageDimensions } from '@/lib/images/dynamic-image';
import { useMediaQuery } from '@/hooks/use-media-query';
import { useEventCourseStoreInfo } from '@/providers/event-course-store-info';
import {
    buildStoreDirectionsUrl,
    formatStoreAddress,
    getDayLabels,
    getStoreHoursGroups,
} from './event-course-store-format';
import { cn } from '@/lib/utils';
import { resolveImageAlt } from '@/lib/images/resolve-image-alt';

type Store = SCAPIShopperStoresSchemas['Store'];

/** Drawer photo: full width of the ~480px desktop panel / full width on mobile. */
const STORE_IMAGE_WIDTHS: DynamicImageDimensions = { base: '100vw', lg: 480 };

/** Body content once the store resolves: photo, name, address, hours and phone. */
function StoreInfoBody({ store }: { store: Store }): ReactElement | null {
    const { t, i18n } = useTranslation('product');
    const address = formatStoreAddress(store);
    const directionsUrl = buildStoreDirectionsUrl(store);
    const telHref = store.phone ? `tel:${store.phone.replace(/\s+/g, '')}` : undefined;

    const hoursGroups = useMemo(
        () => getStoreHoursGroups(store, i18n.language, t('eventCourse.storeInfo.closed'), getDayLabels(i18n.language)),
        [store, i18n.language, t]
    );

    // With no usable store data there is nothing to show.
    if (!store.image && !store.name && !address && hoursGroups.length === 0 && !store.phone) {
        return null;
    }

    return (
        <>
            <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
                {store.image && (
                    <div className="w-full overflow-hidden aspect-[4/3]">
                        <DynamicImage
                            src={store.image}
                            alt={resolveImageAlt({ name: store.name })}
                            className="h-auto w-full object-cover"
                            loading="lazy"
                            widths={STORE_IMAGE_WIDTHS}
                        />
                    </div>
                )}

                <div className="flex flex-col gap-6 px-6 pb-6 pt-6">
                    <div className="flex flex-col gap-2">
                        <p className="label-l uppercase text-neutral-40">{t('eventCourse.storeInfo.kicker')}</p>
                        {store.name && <SheetTitle className="title-m text-foreground">{store.name}</SheetTitle>}
                    </div>

                    <div className="flex flex-col gap-4 rounded-md border border-neutral-80 p-4">
                        {(store.name || address) && (
                            <div className="flex flex-col gap-1">
                                {store.name && <p className="body-copy-m-bold text-neutral-10">{store.name}</p>}
                                {address && <p className="body-copy-m text-neutral-40">{address}</p>}
                            </div>
                        )}

                        {(hoursGroups.length > 0 || store.phone) && (
                            <>
                                <hr className="border-neutral-80" />
                                <div className="flex flex-col gap-3">
                                    {hoursGroups.length > 0 && (
                                        <div className="flex flex-col gap-2">
                                            <p className="label-l uppercase text-neutral-40">
                                                {t('eventCourse.storeInfo.hoursLabel')}
                                            </p>
                                            <dl className="flex flex-col gap-1">
                                                {hoursGroups.map((group) => (
                                                    <div
                                                        key={group.key}
                                                        className="flex justify-between gap-4 body-copy-m text-neutral-10">
                                                        <dt className="shrink-0">{group.label}</dt>
                                                        <dd className="text-right text-neutral-40">{group.hours}</dd>
                                                    </div>
                                                ))}
                                            </dl>
                                        </div>
                                    )}
                                    {store.phone && <p className="body-copy-m text-neutral-10">{store.phone}</p>}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {(directionsUrl || telHref) && (
                <SheetFooter className="shrink-0 border-t border-neutral-80 bg-neutral-white px-6 pb-6 pt-4 sm:flex-row">
                    <div className="flex w-full gap-2">
                        {directionsUrl && (
                            <Button
                                asChild
                                hierarchy="secondary"
                                mode="light"
                                size="large"
                                className="flex-1 uppercase">
                                <NewTabAnchor
                                    href={directionsUrl}
                                    accessibleName={t('eventCourse.storeInfo.directions')}>
                                    {t('eventCourse.storeInfo.directions')}
                                </NewTabAnchor>
                            </Button>
                        )}
                        {telHref && (
                            <Button asChild hierarchy="primary" mode="light" size="large" className="flex-1 uppercase">
                                <a href={telHref}>{t('eventCourse.storeInfo.call')}</a>
                            </Button>
                        )}
                    </div>
                </SheetFooter>
            )}
        </>
    );
}

/** Skeleton fallback while the deferred store promise resolves. */
function StoreInfoSkeleton(): ReactElement {
    return (
        <div className="flex min-h-0 flex-1 animate-pulse flex-col overflow-hidden">
            <div className="aspect-[4/3] w-full bg-sand-95" />
            <div className="flex flex-col gap-6 px-6 pb-6 pt-6">
                <div className="flex flex-col gap-2">
                    <div className="h-3 w-24 rounded bg-sand-95" />
                    <div className="h-6 w-48 rounded bg-sand-95" />
                </div>
                <div className="flex flex-col gap-3 rounded-md border border-neutral-80 p-4">
                    <div className="h-4 w-40 rounded bg-sand-95" />
                    <div className="h-4 w-56 rounded bg-sand-95" />
                    <div className="h-4 w-32 rounded bg-sand-95" />
                </div>
            </div>
        </div>
    );
}

/**
 * Side drawer opened by the booking box "Info e contatti" CTA on event/course
 * PDPs. Shows the associated store (from `c_eventStoreId`) — photo, name,
 * address, opening hours and phone — with directions/call actions. The store is
 * a deferred promise shared via {@link EventCourseStoreInfoProvider}; this
 * component suspends on it and renders nothing if no store is associated.
 */
export default function EventCourseStoreInfoDrawer(): ReactElement {
    const { t } = useTranslation('product');
    const isDesktop = useMediaQuery('(min-width: 1024px)');
    const { eventStore, isDrawerOpen, setDrawerOpen, closeDrawer, openerRef } = useEventCourseStoreInfo();

    // Opened programmatically (no Dialog.Trigger), so Radix would drop focus on the body: return it to the opener.
    const handleCloseAutoFocus = (event: Event) => {
        const opener = openerRef.current;
        if (opener?.isConnected) {
            event.preventDefault();
            opener.focus();
        }
    };

    return (
        <Sheet open={isDrawerOpen} onOpenChange={setDrawerOpen}>
            <SheetContent
                hideClose
                side={isDesktop ? 'right' : 'bottom'}
                aria-describedby="event-course-store-info-description"
                onCloseAutoFocus={handleCloseAutoFocus}
                className={cn(
                    'gap-0 border-neutral-80 bg-neutral-white p-0 focus:outline-none',
                    isDesktop && 'h-full w-full max-w-[480px] border-l sm:max-w-[480px]',
                    !isDesktop && 'max-h-[min(90dvh,720px)] rounded-t-lg border-t'
                )}>
                <SheetDescription id="event-course-store-info-description" className="sr-only">
                    {t('eventCourse.storeInfo.drawerDescription')}
                </SheetDescription>

                <DismissCloseButton
                    aria-label={t('eventCourse.storeInfo.closeDrawer')}
                    onClick={closeDrawer}
                    className="absolute right-4 top-4 z-10 size-10 items-center justify-center rounded-full bg-neutral-white/90 backdrop-blur-sm"
                />

                <Suspense fallback={<StoreInfoSkeleton />}>
                    <Await resolve={eventStore}>{(store) => (store ? <StoreInfoBody store={store} /> : null)}</Await>
                </Suspense>
            </SheetContent>
        </Sheet>
    );
}
